import {ModuleComponent} from "../../js/module-component";

const START_MS = 500;
const TYPING_MS = 900;
const STEP_MS = 1600;
const HOLD_MS = 3200;
const WAVE = [8, 16, 11, 20, 9, 15, 7, 13, 18, 10];

/**
 * Hero Globe: reproduce en bucle las conversaciones de los agentes del mockup y mantiene el vídeo de fondo siempre girando.
 */
export class ModHeroGlobe extends ModuleComponent{

    ready(){
        super.ready();
        this.el = this.$root.get(0);
        if(!this.el) return;

        this.s = "web-mod-hero-globe";
        this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.initVideo();
        this.initScenes();
    }

    /**
     * Relanza el vídeo si el navegador lo pausa o llega al final sin respetar el loop.
     */
    initVideo(){
        this.video = this.findMember("video").get(0);
        if(!this.video) return;
        const v = this.video;
        const kick = () => {
            if(this.visible === false) return;
            v.play().catch(() => {});
        };
        v.muted = true;
        v.loop = true;
        v.addEventListener("pause", kick);
        v.addEventListener("canplay", kick);
        v.addEventListener("ended", () => { v.currentTime = 0; kick(); });
        this.videoWatchdog = setInterval(() => {
            if(this.visible === false) return;
            if(v.ended || (v.duration && v.duration - v.currentTime < 0.15)) v.currentTime = 0;
            if(v.paused) kick();
        }, 250);
        kick();
    }

    initScenes(){
        this.messages = this.findMember("messages").get(0);
        if(!this.messages) return;
        try{
            this.scenes = JSON.parse(this.el.getAttribute("data-scenes") || "[]");
        }catch(e){
            this.scenes = [];
        }
        if(!this.scenes.length) return;

        this.agents = Array.from(this.el.querySelectorAll(".js-mod-hero-globe__agent"));
        this.chatTitle = this.findMember("chat-title").get(0);
        this.connected = this.findMember("connected").get(0);
        this.stats = this.findMember("stats").get(0);

        this.agents.forEach((btn, i) => btn.addEventListener("click", () => this.play(i)));

        // Con movimiento reducido la escena se muestra entera (ya viene pintada del servidor) y solo cambia al hacer clic.
        if(this.reduceMotion) return;

        this.visible = true;
        if("IntersectionObserver" in window){
            this.observer = new IntersectionObserver(entries => {
                const wasVisible = this.visible;
                this.visible = entries[0].isIntersecting;
                if(this.visible && !wasVisible) this.play(this.active || 0);
                if(!this.visible) this.stop();
            });
            this.observer.observe(this.el);
        }
        this.play(0);
    }

    play(index){
        this.stop();
        this.active = index;
        this.step = 0;
        this.typing = false;
        const scene = this.scenes[index];

        this.agents.forEach((btn, i) => {
            btn.classList.toggle("is-active", i === index);
            const bar = btn.querySelector(".js-mod-hero-globe__progress");
            bar.classList.remove("is-running");
            if(i === index && !this.reduceMotion){
                bar.style.setProperty("--hg-duration", this.sceneDuration(scene) + "ms");
                void bar.offsetWidth;
                bar.classList.add("is-running");
            }
        });
        if(this.chatTitle) this.chatTitle.textContent = scene.title;
        if(this.connected) this.connected.textContent = scene.connected;
        this.renderStats(scene);

        if(this.reduceMotion){
            this.messages.textContent = "";
            scene.msgs.forEach(m => this.messages.appendChild(this.createMessage(m[0], m[1])));
            return;
        }
        this.messages.textContent = "";
        this.timer = setTimeout(() => this.next(), START_MS);
    }

    next(){
        const scene = this.scenes[this.active];
        const msgs = scene.msgs;
        if(this.step >= msgs.length){
            this.play((this.active + 1) % this.scenes.length);
            return;
        }
        const kind = msgs[this.step][0];
        if((kind === "agent" || kind === "user") && !this.typing){
            this.typing = true;
            this.showTyping(scene.typing);
            this.timer = setTimeout(() => this.next(), TYPING_MS);
            return;
        }
        this.hideTyping();
        this.typing = false;
        this.messages.appendChild(this.createMessage(kind, msgs[this.step][1]));
        this.step++;
        this.timer = setTimeout(() => this.next(), this.step >= msgs.length ? HOLD_MS : STEP_MS);
    }

    stop(){
        clearTimeout(this.timer);
        this.timer = null;
    }

    /**
     * Duración total de una escena; marca la velocidad de la barra de progreso del agente activo.
     * @param {Object} scene
     * @returns {number}
     */
    sceneDuration(scene){
        const typed = scene.msgs.filter(m => m[0] === "agent" || m[0] === "user").length;
        return START_MS + typed * TYPING_MS + (scene.msgs.length - 1) * STEP_MS + HOLD_MS;
    }

    showTyping(text){
        this.hideTyping();
        const el = document.createElement("div");
        el.className = `${this.s}__typing`;
        const dots = document.createElement("span");
        dots.className = `${this.s}__typing-dots`;
        dots.setAttribute("aria-hidden", "true");
        for(let i = 0; i < 3; i++) dots.appendChild(document.createElement("span"));
        el.appendChild(dots);
        el.appendChild(document.createTextNode(text));
        this.messages.appendChild(el);
        this.typingEl = el;
    }

    hideTyping(){
        if(this.typingEl){
            this.typingEl.remove();
            this.typingEl = null;
        }
    }

    createMessage(kind, text){
        const s = this.s;
        const el = document.createElement("div");
        el.className = `${s}__msg ${s}__msg--${kind}`;
        if(kind === "audio"){
            const play = document.createElement("span");
            play.className = `${s}__audio-play`;
            play.setAttribute("aria-hidden", "true");
            play.textContent = "▶";
            const wave = document.createElement("span");
            wave.className = `${s}__audio-wave`;
            wave.setAttribute("aria-hidden", "true");
            WAVE.forEach(h => {
                const bar = document.createElement("span");
                bar.style.height = h + "px";
                wave.appendChild(bar);
            });
            const label = document.createElement("span");
            label.className = `${s}__audio-label`;
            label.textContent = text;
            el.append(play, wave, label);
        }else if(kind === "action"){
            const check = document.createElement("span");
            check.className = `${s}__accent`;
            check.setAttribute("aria-hidden", "true");
            check.textContent = "✓";
            el.append(check, document.createTextNode(text));
        }else{
            el.textContent = text;
        }
        return el;
    }

    renderStats(scene){
        if(!this.stats) return;
        const s = this.s;
        this.stats.textContent = "";
        scene.stats.forEach(stat => {
            const card = document.createElement("div");
            card.className = `${s}__stat`;
            const v = document.createElement("span");
            v.className = `${s}__stat-value`;
            v.textContent = stat.v;
            const l = document.createElement("span");
            l.className = `${s}__stat-label`;
            l.textContent = stat.l;
            card.append(v, l);
            this.stats.appendChild(card);
        });
    }

    destroy(){
        this.stop();
        if(this.videoWatchdog) clearInterval(this.videoWatchdog);
        if(this.observer) this.observer.disconnect();
        if(super.destroy) super.destroy();
    }
}
